import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import HostMaintenancePanel from '../components/HostMaintenancePanel';
import ScopedSelfUpdate from './toolBrowser/ScopedSelfUpdate';
import type {
  DeleteWorkerNodeResponse,
  TriggerWorkerHelperResponse,
  WorkerHelperJobSummary,
  WorkerNode,
  WorkerToken
} from '../types';
import { reportErrorLocal } from '../utils/errors';
import './WorkerManagementPage.lane50.css';

type Props = {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
  canOperate: boolean;
  canViewNodes: boolean;
  canMaintainHosts: boolean;
  canConfigureTopology: boolean;
};

type ExecutorInstallProfile = 'conversation' | 'production';

type GeneratedExecutorInstall = {
  command: string;
  profile: ExecutorInstallProfile;
  apiKeyId: string;
  expiresAt?: string | null;
};

type UserApiKeyResponse = {
  id: string;
  token?: string | null;
  expires_at?: string | null;
};

type ManagedStorageInstallOptions = {
  dataRoot: string;
  snapshotRoot: string;
  filesystemUuid: string;
  minFreePercent: number;
  requireRaid1: boolean;
};

const profileDefaultTags: Record<ExecutorInstallProfile, string[]> = {
  conversation: ['dev'],
  production: ['production', 'infrastructure']
};

const EXECUTOR_HEARTBEAT_STALE_AFTER_SECONDS = 120;
const SELF_UPDATE_STATUS_WATCH_MS = 20 * 60 * 1000;
const EXECUTOR_STATUS_REFRESH_MS = 30 * 1000;

type HeartbeatSnapshot = {
  ageSeconds: number;
  observedAt: number;
};

type UpdateWatch = {
  workerId: string;
  until: number;
  waitingForReconnect: boolean;
};

type LoadExecutorsOptions = {
  foreground?: boolean;
};

function executorSortKey(node: WorkerNode) {
  return (node.name?.trim() || node.id).toLocaleLowerCase();
}

function compareExecutors(left: WorkerNode, right: WorkerNode) {
  if (left.system_default !== right.system_default) {
    return left.system_default ? -1 : 1;
  }
  const leftKey = executorSortKey(left);
  const rightKey = executorSortKey(right);
  if (leftKey < rightKey) return -1;
  if (leftKey > rightKey) return 1;
  return left.id.localeCompare(right.id);
}

function executorOwnerLabel(node: WorkerNode) {
  if (node.browser_owned_by_actor !== undefined) return node.browser_owned_by_actor ? 'Owned by originating actor' : 'System default (scoped read only)';
  if (!node.owner) return 'System managed';
  return node.owner.name?.trim() || node.owner.email?.trim() || `User ${node.owner.id.slice(0, 8)}`;
}

function executorOwnerDetail(node: WorkerNode) {
  if (!node.owner) return null;
  if (node.owner.name?.trim() && node.owner.email?.trim()) return node.owner.email.trim();
  if (!node.owner.name?.trim() && !node.owner.email?.trim()) return node.owner.id;
  return null;
}

const firecrackerCheckLabels: Record<string, string> = {
  firecracker_binary: 'firecracker',
  jailer_binary: 'jailer',
  state_root: 'state root',
  copy_on_write_reflink: 'copy-on-write',
  full_copy_fallback_allowed: 'full-copy fallback',
  kernel_image: 'kernel image',
  kernel_mmio_cmdline_devices: 'kernel block device',
  rootfs_template: 'rootfs template',
  runner_executable: 'VM runner',
  kvm_device: '/dev/kvm',
  kvm_access: 'KVM access',
  cpu_virtualization: 'CPU virtualization',
  kvm_module: 'KVM module',
  cgroup_fs: 'cgroups',
  tun_device: '/dev/net/tun',
  ip_tool: 'ip tool',
  firewall_tool: 'iptables/nft'
};

const firecrackerRequiredChecks = new Set([
  'firecracker_binary',
  'state_root',
  'kernel_image',
  'kernel_mmio_cmdline_devices',
  'rootfs_template',
  'runner_executable',
  'kvm_device',
  'kvm_access',
  'kvm_module',
  'cgroup_fs'
]);

const firecrackerAdvisoryChecks = new Set([
  'jailer_binary',
  'cpu_virtualization',
  'tun_device',
  'ip_tool',
  'firewall_tool'
]);

function formatTimestamp(value?: string | null) {
  return value ? new Date(value).toLocaleString() : '-';
}

function executorTokenExpired(token: WorkerToken) {
  return Boolean(token.expires_at && new Date(token.expires_at).getTime() <= Date.now());
}

function shortHash(value?: string | null) {
  if (!value || value === 'unknown') return 'unknown';
  return value.slice(0, 8);
}

function capabilityEnabled(value: unknown): boolean {
  return value === true;
}

function firecrackerCheckSeverity(
  key: string,
  ok: boolean,
  present: boolean,
  checks: Record<string, unknown>
): 'ok' | 'warning' | 'error' | 'neutral' {
  if (!present) return 'neutral';
  const fullCopyAllowed = capabilityEnabled(checks.full_copy_fallback_allowed);
  const reflinkOk = capabilityEnabled(checks.copy_on_write_reflink);
  if (key === 'copy_on_write_reflink') {
    if (ok) return 'ok';
    return fullCopyAllowed ? 'warning' : 'error';
  }
  if (key === 'full_copy_fallback_allowed') {
    if (reflinkOk) return ok ? 'warning' : 'neutral';
    return ok ? 'warning' : 'neutral';
  }
  if (ok) return 'ok';
  if (firecrackerRequiredChecks.has(key)) return 'error';
  if (firecrackerAdvisoryChecks.has(key)) return 'warning';
  return 'neutral';
}

function firecrackerChecks(
  node: WorkerNode
): Array<{ key: string; label: string; ok: boolean; severity: 'ok' | 'warning' | 'error' | 'neutral' }> {
  const checks = node.capabilities?.firecracker_checks;
  if (!checks || typeof checks !== 'object' || Array.isArray(checks)) {
    return [];
  }
  const checkValues = checks as Record<string, unknown>;
  return Object.entries(firecrackerCheckLabels).map(([key, label]) => ({
    key,
    label,
    ok: capabilityEnabled(checkValues[key]),
    severity: firecrackerCheckSeverity(
      key,
      capabilityEnabled(checkValues[key]),
      Object.prototype.hasOwnProperty.call(checkValues, key),
      checkValues
    )
  }));
}

function firecrackerSummary(node: WorkerNode): { label: string; className: string } {
  if (capabilityEnabled(node.capabilities?.firecracker)) {
    return { label: 'ready', className: 'badge badge--success' };
  }
  const checks = firecrackerChecks(node);
  if (checks.length === 0) {
    return { label: 'not reported', className: 'badge' };
  }
  const missing = checks.filter((check) => check.severity === 'error').length;
  const warnings = checks.filter((check) => check.severity === 'warning').length;
  if (missing === 0 && warnings > 0) {
    return { label: `${warnings} warning${warnings === 1 ? '' : 's'}`, className: 'badge badge--warning' };
  }
  return {
    label: missing === 0 ? 'disabled' : `${missing} missing`,
    className: missing === 0 ? 'badge badge--warning' : 'badge badge--error'
  };
}

function checkTextClass(severity: 'ok' | 'warning' | 'error' | 'neutral') {
  if (severity === 'ok') return 'status-text--ok';
  if (severity === 'error') return 'status-text--missing';
  if (severity === 'warning') return 'warning-text';
  return 'hint';
}

function checkDotClass(severity: 'ok' | 'warning' | 'error' | 'neutral') {
  if (severity === 'ok') return 'status-dot status-dot--ok';
  if (severity === 'error') return 'status-dot status-dot--missing';
  if (severity === 'warning') return 'status-dot status-dot--incomplete';
  return 'status-dot';
}

function statusClass(status: string) {
  const normalized = status.toLowerCase();
  if (normalized === 'online') return 'badge badge--success';
  if (normalized === 'busy') return 'badge badge--processing';
  if (normalized === 'stale') return 'badge badge--error';
  if (normalized === 'offline') return 'badge';
  return 'badge badge--warning';
}

function monotonicNow() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function heartbeatAgeSeconds(node: WorkerNode, snapshot?: HeartbeatSnapshot, now = monotonicNow()) {
  if (snapshot) {
    return Math.max(0, Math.floor(snapshot.ageSeconds + (now - snapshot.observedAt) / 1000));
  }
  if (typeof node.heartbeat_age_seconds === 'number' && Number.isFinite(node.heartbeat_age_seconds)) {
    return Math.max(0, Math.round(node.heartbeat_age_seconds));
  }
  const lastSeen = new Date(node.last_seen_at).getTime();
  if (Number.isFinite(lastSeen)) return Math.max(0, Math.floor((Date.now() - lastSeen) / 1000));
  return null;
}

function heartbeatFresh(node: WorkerNode, snapshot?: HeartbeatSnapshot, now = monotonicNow()) {
  const age = heartbeatAgeSeconds(node, snapshot, now);
  return age !== null ? age <= EXECUTOR_HEARTBEAT_STALE_AFTER_SECONDS : node.heartbeat_fresh === true;
}

function statusLabel(node: WorkerNode, fresh: boolean) {
  return node.status.toLowerCase() === 'online' && !fresh ? 'stale' : node.status;
}

function heartbeatHint(node: WorkerNode, snapshot?: HeartbeatSnapshot, now = monotonicNow()) {
  const age = heartbeatAgeSeconds(node, snapshot, now);
  if (age === null) return 'heartbeat age unknown';
  if (age < 60) return `last check-in ${age}s ago`;
  const minutes = Math.floor(age / 60);
  return `last check-in ${minutes}m ${age % 60}s ago`;
}

function runnerStatus(node: WorkerNode, awaitingReconnect: boolean) {
  if (node.runner_current === undefined) return { label: 'build comparison unavailable', className: 'badge' };
  if (node.runner_current) return { label: 'current', className: 'badge badge--success' };
  const updateStatus = node.self_update?.status?.toLowerCase();
  if (updateStatus === 'queued') return { label: 'update queued', className: 'badge badge--processing' };
  if (updateStatus === 'running') return { label: 'updating', className: 'badge badge--processing' };
  if (updateStatus === 'succeeded') {
    return {
      label: awaitingReconnect ? 'awaiting reconnect' : 'outdated; last update succeeded',
      className: 'badge badge--warning'
    };
  }
  if (updateStatus === 'failed' || updateStatus === 'canceled') {
    return { label: `update ${updateStatus}`, className: 'badge badge--error' };
  }
  return { label: 'outdated', className: 'badge badge--warning' };
}

function executorAvailabilityLabel(node: WorkerNode, fresh: boolean) {
  if (!node.enabled) return { label: 'disabled', className: 'badge badge--error' };
  if (!fresh) return { label: 'stale; not responding', className: 'badge badge--error' };
  if (!node.conversation_enabled) return { label: 'draining', className: 'badge badge--warning' };
  return { label: 'accepting conversations', className: 'badge badge--success' };
}

function helperStatusClass(status?: string | null) {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'succeeded') return 'badge badge--success';
  if (normalized === 'failed' || normalized === 'canceled') return 'badge badge--error';
  if (normalized === 'queued' || normalized === 'running') return 'badge badge--processing';
  return 'badge';
}

function selfUpdateActive(node: WorkerNode) {
  const status = node.self_update?.status?.toLowerCase();
  return status === 'queued' || status === 'running';
}

function selfUpdateTerminal(status?: string | null) {
  const normalized = (status || '').toLowerCase();
  return normalized === 'succeeded' || normalized === 'failed' || normalized === 'canceled';
}

function canSelfUpdate(node: WorkerNode) {
  return (
    node.enabled &&
    capabilityEnabled(node.capabilities?.infrastructure_helpers) &&
    capabilityEnabled(node.capabilities?.self_update) &&
    !selfUpdateActive(node)
  );
}

function optimisticSelfUpdate(
  response: TriggerWorkerHelperResponse,
  previous?: WorkerHelperJobSummary | null
): WorkerHelperJobSummary {
  const now = new Date().toISOString();
  return {
    id: response.id,
    helper: response.helper,
    status: response.status,
    created_at: previous?.id === response.id ? previous.created_at : now,
    leased_at: previous?.id === response.id ? previous.leased_at : response.status.toLowerCase() === 'running' ? now : null,
    finished_at: previous?.id === response.id ? previous.finished_at : null,
    last_error: previous?.id === response.id ? previous.last_error : null,
    result: previous?.id === response.id ? previous.result : null
  };
}

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function shellQuote(value: string) {
  return `'${value.replace(/'/g, `'"'"'`)}'`;
}

function splitTags(value: string) {
  return value
    .split(/[,\s]+/)
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function buildExecutorInstallCommand(
  profile: ExecutorInstallProfile,
  backendUrl: string,
  apiKey: string,
  name: string | null,
  tags: string[],
  managedStorage?: ManagedStorageInstallOptions
) {
  const runtime = profile === 'conversation' ? 'firecracker' : 'infrastructure';
  const cliPath = '/usr/local/bin/proj-creator-runner-cli';
  const args = [
    'install-executor',
    '--backend-url',
    backendUrl,
    '--api-key',
    apiKey,
    ...(name ? ['--name', name] : []),
    '--instance-name',
    profile,
    '--yes',
    ...tags.flatMap((tag) => ['--tag', tag]),
    ...(profile === 'production' && managedStorage
      ? [
          '--managed-data-root',
          managedStorage.dataRoot,
          '--managed-snapshot-root',
          managedStorage.snapshotRoot,
          ...(managedStorage.filesystemUuid
            ? ['--managed-data-filesystem-uuid', managedStorage.filesystemUuid]
            : []),
          '--managed-data-min-free-percent',
          String(managedStorage.minFreePercent),
          ...(managedStorage.requireRaid1 ? ['--managed-data-require-raid1'] : [])
        ]
      : [])
  ];
  const ensureCliCommand = [
    `if [ -x ${shellQuote(cliPath)} ]; then :; else`,
    `curl -fsSL ${shellQuote(`${backendUrl}/api/cli/install.sh`)} | sudo env PROJ_CREATOR_INSTALL_DIR=/usr/local/bin sh;`,
    'fi'
  ].join(' ');
  return [
    ensureCliCommand,
    `sudo ${shellQuote(cliPath)} 'self-update' '--backend-url' ${shellQuote(backendUrl)} '--install-path' ${shellQuote(cliPath)} '--yes'`,
    `sudo env PROJ_CREATOR_EXECUTOR_AGENT_RUNTIME=${shellQuote(runtime)} ${shellQuote(cliPath)} ${args.map(shellQuote).join(' ')}`
  ].join(' && ');
}

export default function WorkerManagementPage({
  apiClient,
  setError,
  canOperate: ordinaryCanOperate,
  canViewNodes,
  canMaintainHosts: ordinaryCanMaintainHosts,
  canConfigureTopology: ordinaryCanConfigureTopology
}: Props) {
  const scoped = apiClient.mode === 'tool-browser';
  const canOperate = !scoped && ordinaryCanOperate;
  const canMaintainHosts = !scoped && ordinaryCanMaintainHosts;
  const canConfigureTopology = !scoped && ordinaryCanConfigureTopology;
  const [nodes, setNodes] = useState<WorkerNode[]>([]);
  const [tokens, setTokens] = useState<WorkerToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [revokingToken, setRevokingToken] = useState<string | null>(null);
  const [installProfile, setInstallProfile] = useState<ExecutorInstallProfile>('conversation');
  const [installName, setInstallName] = useState('');
  const [installTags, setInstallTags] = useState(profileDefaultTags.conversation.join(', '));
  const [managedDataRoot, setManagedDataRoot] = useState('/create-proj/apps');
  const [managedSnapshotRoot, setManagedSnapshotRoot] = useState('/srv/snapshots');
  const [managedFilesystemUuid, setManagedFilesystemUuid] = useState('');
  const [managedMinFreePercent, setManagedMinFreePercent] = useState(15);
  const [managedRequireRaid1, setManagedRequireRaid1] = useState(true);
  const [generatedInstall, setGeneratedInstall] = useState<GeneratedExecutorInstall | null>(null);
  const [generatingInstall, setGeneratingInstall] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkUpdateStatus, setBulkUpdateStatus] = useState<string | null>(null);
  const [updateWatch, setUpdateWatch] = useState<UpdateWatch | null>(null);
  const updateWatchRef = useRef<UpdateWatch | null>(null);
  const loadRequestVersion = useRef(0);
  const foregroundRequestVersion = useRef<number | null>(null);
  const heartbeatSnapshots = useRef(new Map<string, HeartbeatSnapshot>());

  // Keep heartbeat labels honest between API refreshes. The server value is a
  // snapshot; a monotonic browser clock advances it without trusting client
  // wall-clock skew.
  const [heartbeatClock, setHeartbeatClock] = useState(() => monotonicNow());

  const loadWorkers = useCallback(async ({ foreground = false }: LoadExecutorsOptions = {}) => {
    const requestVersion = ++loadRequestVersion.current;
    if (foreground) {
      foregroundRequestVersion.current = requestVersion;
      setLoading(true);
    }
    try {
      const [nodeRows, tokenRows] = await Promise.all([
        canViewNodes
          ? apiClient.get<WorkerNode[]>('/api/executors/nodes')
          : Promise.resolve([]),
        canOperate
          ? apiClient.get<WorkerToken[]>('/api/executors/tokens')
          : Promise.resolve([])
      ]);
      if (requestVersion !== loadRequestVersion.current) return;
      const rows = [...(nodeRows ?? [])].sort(compareExecutors);
      const watchedUpdate = updateWatchRef.current;
      const watchedNode = watchedUpdate
        ? rows.find((node) => node.id === watchedUpdate.workerId)
        : null;
      const watchedUpdateStatus = watchedNode?.self_update?.status?.toLowerCase();
      const updateDelivered = Boolean(
        watchedUpdate?.waitingForReconnect &&
        watchedNode &&
        (watchedNode.runner_current || (watchedUpdateStatus && watchedUpdateStatus !== 'queued'))
      );
      if (updateDelivered && watchedNode && watchedUpdate) {
        const watchedWorkerId = watchedUpdate.workerId;
        setMessage(
          watchedNode.runner_current
            ? 'Executor reconnected and the self-update completed.'
            : watchedUpdateStatus === 'running'
              ? 'Executor reconnected and the self-update is running.'
              : watchedUpdateStatus === 'succeeded'
                ? 'Executor self-update finished; it still reports an older runner.'
                : watchedUpdateStatus === 'failed'
                  ? 'Executor reconnected, but the self-update failed.'
                  : 'Executor reconnected and accepted the self-update.'
        );
        setUpdateWatch((current) => {
          const next = current?.workerId === watchedWorkerId
            ? { ...current, waitingForReconnect: false }
            : current;
          updateWatchRef.current = next;
          return next;
        });
      }
      const observedAt = monotonicNow();
      const currentIds = new Set(rows.map((node) => node.id));
      for (const id of heartbeatSnapshots.current.keys()) {
        if (!currentIds.has(id)) heartbeatSnapshots.current.delete(id);
      }
      rows.forEach((node) => {
        const age = typeof node.heartbeat_age_seconds === 'number' && Number.isFinite(node.heartbeat_age_seconds)
          ? Math.max(0, node.heartbeat_age_seconds)
          : null;
        if (age !== null) {
          heartbeatSnapshots.current.set(node.id, { ageSeconds: age, observedAt });
        } else {
          heartbeatSnapshots.current.delete(node.id);
        }
      });
      setNodes(rows);
      setTokens(tokenRows ?? []);
      setHasLoaded(true);
      setLoadFailed(false);
      setLocalError(null);
    } catch (err) {
      if (requestVersion !== loadRequestVersion.current) return;
      setLoadFailed(true);
      reportErrorLocal('Failed to load executors', err, setError, setLocalError);
    } finally {
      if (foregroundRequestVersion.current === requestVersion) {
        foregroundRequestVersion.current = null;
        setLoading(false);
      }
    }
  }, [apiClient, canOperate, canViewNodes, setError]);

  useEffect(() => {
    void loadWorkers({ foreground: true });
  }, [loadWorkers]);

  useEffect(() => {
    const handle = window.setInterval(() => setHeartbeatClock(monotonicNow()), 1000);
    return () => window.clearInterval(handle);
  }, []);

  useEffect(() => {
    const handle = window.setInterval(() => void loadWorkers(), EXECUTOR_STATUS_REFRESH_MS);
    return () => window.clearInterval(handle);
  }, [loadWorkers]);

  useEffect(() => {
    const watchedNode = updateWatch
      ? nodes.find((node) => node.id === updateWatch.workerId)
      : null;
    const watchPending = Boolean(
      updateWatch &&
      Date.now() < updateWatch.until &&
      watchedNode &&
      !watchedNode.runner_current
    );
    if (!nodes.some(selfUpdateActive) && !watchPending) {
      return;
    }
    const handle = window.setInterval(() => {
      if (updateWatch && Date.now() >= updateWatch.until) {
        updateWatchRef.current = null;
        setUpdateWatch(null);
        return;
      }
      void loadWorkers();
    }, 2500);
    return () => window.clearInterval(handle);
  }, [loadWorkers, nodes, updateWatch]);

  const heartbeatFreshFor = useCallback(
    (node: WorkerNode) => heartbeatFresh(node, heartbeatSnapshots.current.get(node.id), heartbeatClock),
    [heartbeatClock]
  );

  const heartbeatHintFor = useCallback(
    (node: WorkerNode) => heartbeatHint(node, heartbeatSnapshots.current.get(node.id), heartbeatClock),
    [heartbeatClock]
  );

  const summary = useMemo(() => {
    const busy = nodes.filter((node) => node.status.toLowerCase() === 'busy').length;
    const online = nodes.filter((node) => node.status.toLowerCase() === 'online' && heartbeatFreshFor(node)).length;
    const firecrackerReady = nodes.filter((node) => capabilityEnabled(node.capabilities?.firecracker)).length;
    const dockerReady = nodes.filter((node) => capabilityEnabled(node.capabilities?.docker)).length;
    const systemDefault = nodes.filter((node) => node.system_default).length;
    const outdated = nodes.filter((node) => node.runner_current === false).length;
    const updating = nodes.filter(selfUpdateActive).length;
    return { busy, online, firecrackerReady, dockerReady, systemDefault, outdated, updating };
  }, [heartbeatFreshFor, nodes]);

  const deleteNode = async (node: WorkerNode) => {
    if (node.current_job_id || node.current_run_id) {
      setLocalError('Executor is currently assigned to a job.');
      return;
    }
    if (!window.confirm(`Delete executor record "${node.name || node.id}"?`)) {
      return;
    }
    setDeleting(node.id);
    setMessage(null);
    try {
      const response = await apiClient.del<DeleteWorkerNodeResponse>(
        `/api/executors/nodes/${encodeURIComponent(node.id)}`
      );
      setMessage(response.deleted ? 'Executor record deleted.' : 'Executor record was already gone.');
      await loadWorkers();
    } catch (err) {
      reportErrorLocal('Failed to delete executor record', err, setError, setLocalError);
    } finally {
      setDeleting(null);
    }
  };

  const revokeToken = async (token: WorkerToken) => {
    if (token.revoked_at) {
      return;
    }
    if (!window.confirm(`Revoke executor token "${token.name || token.id}"?`)) {
      return;
    }
    setRevokingToken(token.id);
    setMessage(null);
    try {
      await apiClient.post(`/api/executors/tokens/${encodeURIComponent(token.id)}/revoke`, {});
      setMessage('Executor token revoked.');
      await loadWorkers();
    } catch (err) {
      reportErrorLocal('Failed to revoke executor token', err, setError, setLocalError);
    } finally {
      setRevokingToken(null);
    }
  };

  const queueSelfUpdate = async (node: WorkerNode, drainBeforeUpdate: boolean, source: string) => {
    if (!capabilityEnabled(node.capabilities?.infrastructure_helpers)) {
      setLocalError('Executor does not support infrastructure helpers.');
      return null;
    }
    if (!capabilityEnabled(node.capabilities?.self_update)) {
      setLocalError('Executor self-update is disabled by configuration.');
      return null;
    }
    setUpdating(node.id);
    setMessage(null);
    try {
      const response = await apiClient.post<TriggerWorkerHelperResponse>(
        `/api/executors/nodes/${encodeURIComponent(node.id)}/self-update`,
        { payload: { source }, drain_before_update: drainBeforeUpdate }
      );
      const responseStatus = response.status.toLowerCase();
      setNodes((current) => current.map((row) => row.id === node.id
        ? { ...row, self_update: optimisticSelfUpdate(response, row.self_update) }
        : row));
      const reconnectPending = responseStatus === 'queued' && !heartbeatFreshFor(node);
      const watch = {
        workerId: node.id,
        until: Date.now() + SELF_UPDATE_STATUS_WATCH_MS,
        waitingForReconnect: reconnectPending
      };
      updateWatchRef.current = watch;
      setUpdateWatch(watch);
      const label = responseStatus === 'running'
        ? 'already running'
        : reconnectPending
          ? 'queued; not delivered until reconnect'
          : response.status;
      const liveness = reconnectPending
        ? ` The executor has not checked in for ${heartbeatHintFor(node)}; it cannot consume the request until it reconnects.`
        : '';
      setMessage(
        drainBeforeUpdate
          ? `Executor self-update ${label}; conversation processing is drained while the update runs.${liveness}`
          : `Executor self-update ${label}.${liveness}`
      );
      await loadWorkers();
      return response;
    } catch (err) {
      reportErrorLocal('Failed to queue executor self-update', err, setError, setLocalError);
      return null;
    } finally {
      setUpdating(null);
    }
  };

  const forceUpdateNode = async (node: WorkerNode) => {
    await queueSelfUpdate(node, false, 'manual_ui_force');
  };

  const drainAndUpdateNode = async (node: WorkerNode) => {
    await queueSelfUpdate(node, true, 'manual_ui_drain');
  };

  const waitForSelfUpdate = async (
    workerId: string,
    helperId: string,
    onStatus?: (node: WorkerNode, helper: WorkerNode['self_update']) => void
  ) => {
    const deadline = Date.now() + 20 * 60 * 1000;
    while (Date.now() < deadline) {
      await delay(2500);
      const current = await apiClient.get<WorkerNode>(
        `/api/executors/nodes/${encodeURIComponent(workerId)}`
      );
      const helper = current.self_update;
      onStatus?.(current, helper);
      if (helper?.id === helperId && selfUpdateTerminal(helper.status)) return current;
    }
    throw new Error(
      'Timed out waiting for executor self-update to finish. It may still be queued if the executor is busy or not polling helper leases.'
    );
  };

  const drainAndUpdateAll = async () => {
    if (bulkUpdating) return;
    const targets = nodes.filter(
      (node) => node.access_level !== 'terminal' && !node.runner_current && canSelfUpdate(node)
    );
    if (targets.length === 0) {
      setMessage('No outdated enabled executors are ready for drain-and-update.');
      return;
    }
    setBulkUpdating(true);
    setLocalError(null);
    setMessage(null);
    try {
      for (const [index, node] of targets.entries()) {
        const label = node.name || node.id;
        setBulkUpdateStatus(`Queueing ${index + 1} of ${targets.length}: ${label}`);
        setUpdating(node.id);
        const response = await apiClient.post<TriggerWorkerHelperResponse>(
          `/api/executors/nodes/${encodeURIComponent(node.id)}/self-update`,
          { payload: { source: 'manual_ui_bulk_drain' }, drain_before_update: true }
        );
        setBulkUpdateStatus(
          `Waiting for ${index + 1} of ${targets.length}: ${label} self-update ${response.status}`
        );
        const updated = await waitForSelfUpdate(node.id, response.id, (current, helper) => {
          if (helper?.id !== response.id) {
            setBulkUpdateStatus(
              `Waiting for ${index + 1} of ${targets.length}: ${label} self-update to appear`
            );
            return;
          }
          const state = helper.status === 'queued'
            ? 'queued; waiting for executor to become idle and lease it'
            : helper.status === 'running'
              ? 'running on executor'
              : helper.status;
          const busy = current.current_job_id || current.current_run_id
            ? ' (executor is busy)'
            : '';
          setBulkUpdateStatus(
            `Updating ${index + 1} of ${targets.length}: ${label} self-update ${state}${busy}`
          );
        });
        if (updated.self_update?.status === 'failed' || updated.self_update?.status === 'canceled') {
          throw new Error(
            `${label} self-update ${updated.self_update.status}: ${updated.self_update.last_error || 'no error detail'}`
          );
        }
        setUpdating(null);
        await loadWorkers();
      }
      setMessage(
        `Drain-and-update completed for ${targets.length} executor${targets.length === 1 ? '' : 's'}.`
      );
    } catch (error) {
      reportErrorLocal('Drain-and-update all failed', error, setError, setLocalError);
    } finally {
      setUpdating(null);
      setBulkUpdating(false);
      setBulkUpdateStatus(null);
    }
  };

  const setSystemDefault = async (node: WorkerNode, systemDefault: boolean) => {
    setUpdating(node.id);
    setMessage(null);
    try {
      await apiClient.post(`/api/executors/nodes/${encodeURIComponent(node.id)}/system-default`, {
        system_default: systemDefault
      });
      setMessage(systemDefault ? 'Executor marked as a system default.' : 'Executor removed from system defaults.');
      await loadWorkers();
    } catch (err) {
      reportErrorLocal('Failed to update executor default status', err, setError, setLocalError);
    } finally {
      setUpdating(null);
    }
  };

  const selectInstallProfile = (profile: ExecutorInstallProfile) => {
    setInstallProfile(profile);
    setInstallTags(profileDefaultTags[profile].join(', '));
    setGeneratedInstall(null);
  };

  const copyText = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied((current) => (current === label ? null : current)), 1500);
    } catch (err) {
      reportErrorLocal(`Failed to copy ${label}`, err, setError, setLocalError);
    }
  };

  const generateInstallCommand = async () => {
    const tags = splitTags(installTags);
    if (tags.length === 0) {
      setLocalError('Install command needs at least one executor tag.');
      return;
    }
    const backendUrl = window.location.origin.replace(/\/+$/, '');
    const name = installName.trim() || null;
    const managedStorage = installProfile === 'production'
      ? {
          dataRoot: managedDataRoot.trim(),
          snapshotRoot: managedSnapshotRoot.trim(),
          filesystemUuid: managedFilesystemUuid.trim(),
          minFreePercent: managedMinFreePercent,
          requireRaid1: managedRequireRaid1
        }
      : undefined;
    if (managedStorage && (!managedStorage.dataRoot || !managedStorage.snapshotRoot)) {
      setLocalError('Production managed storage needs both a data root and snapshot root.');
      return;
    }
    if (managedStorage && (managedStorage.minFreePercent < 5 || managedStorage.minFreePercent > 50)) {
      setLocalError('Managed storage minimum free space must be between 5% and 50%.');
      return;
    }
    setGeneratingInstall(true);
    setMessage(null);
    try {
      const apiKey = await apiClient.post<UserApiKeyResponse>('/api/me/api-keys', {
        name: `executor install ${installProfile} ${new Date().toISOString()}`,
        scopes: [
          'executors:view',
          'executors:operate',
          'executors:terminal',
          'executors:terminal:privileged'
        ],
        expires_in_minutes: 10
      });
      if (!apiKey.token) {
        throw new Error('API key creation response did not include the API key secret.');
      }
      const command = buildExecutorInstallCommand(
        installProfile,
        backendUrl,
        apiKey.token,
        name,
        tags,
        managedStorage
      );
      setGeneratedInstall({ command, profile: installProfile, apiKeyId: apiKey.id, expiresAt: apiKey.expires_at });
      setMessage(
        'Executor install command copied. The temporary API key expires in 10 minutes; each host creates its own executor token when the command runs.'
      );
      await copyText('install command', command);
    } catch (err) {
      reportErrorLocal('Failed to create executor install command', err, setError, setLocalError);
    } finally {
      setGeneratingInstall(false);
    }
  };

  return (
    <section className="stack worker-management-page" aria-label="Executor management">
      {scoped && <div className="card full" role="status">Tool-token scoped executor inventory. Only separately authorized fixed self-update controls are available. Enrollment, credentials, general operations, topology, helper/workload history and build comparison are unavailable. Capability advertisements are not permissions.</div>}
      <div className="card full worker-management-overview">
        <div className="card-header">
          <div>
            <h2>Executors</h2>
            <p className="status">
              Manage persistent executors, inspect runtime capabilities, and remove stale executor
              records.
            </p>
          </div>
          <div className="row row--compact">
            {canOperate && (
              <button
                type="button"
                onClick={() => void drainAndUpdateAll()}
                disabled={bulkUpdating || loading}
              >
                {bulkUpdating ? 'Updating...' : 'Drain and update all'}
              </button>
            )}
            <button
              type="button"
              className="secondary"
              onClick={() => void loadWorkers({ foreground: true })}
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
        <nav className="worker-management-nav" aria-label="Executor sections">
          {canViewNodes && <a href="#executor-nodes">Executor nodes</a>}
          {canOperate && <a href="#executor-install">Add executor</a>}
          {canOperate && <a href="#executor-tokens">Executor tokens</a>}
          {canMaintainHosts && <a href="#host-maintenance">Host maintenance</a>}
        </nav>
        {localError && <div role="alert"><ErrorBanner message={localError} onDismiss={() => setLocalError(null)} /></div>}
        <p className="status" role="status">
          {loading
            ? hasLoaded ? 'Refreshing executor inventory. Previous records remain visible.' : 'Loading executor inventory…'
            : loadFailed
              ? hasLoaded ? 'Refresh failed. Showing previous records; use Refresh to retry.' : 'Inventory unavailable. Use Refresh to retry.'
              : 'Inventory refreshes automatically every 30 seconds.'}
        </p>
        {message && <p className="status status-text--ok" role="status">{message}</p>}
        {bulkUpdateStatus && <p className="status" role="status">{bulkUpdateStatus}</p>}
        {canViewNodes && hasLoaded && <div className="stats-grid" aria-label="Last loaded executor summary">
          <div className="stat">
            <span className="stat-value">{nodes.length}</span>
            <span className="stat-label">Registered</span>
          </div>
          <div className="stat">
            <span className="stat-value">{summary.online}</span>
            <span className="stat-label">Online</span>
          </div>
          <div className="stat">
            <span className="stat-value">{summary.busy}</span>
            <span className="stat-label">Busy</span>
          </div>
          <div className="stat">
            <span className="stat-value">{summary.dockerReady}</span>
            <span className="stat-label">Docker</span>
          </div>
          <div className="stat">
            <span className="stat-value">{summary.firecrackerReady}</span>
            <span className="stat-label">Firecracker</span>
          </div>
          <div className="stat">
            <span className="stat-value">{summary.systemDefault}</span>
            <span className="stat-label">System default</span>
          </div>
          {!scoped && <><div className="stat">
            <span className="stat-value">{summary.outdated}</span>
            <span className="stat-label">Outdated</span>
          </div>
          <div className="stat">
            <span className="stat-value">{summary.updating}</span>
            <span className="stat-label">Updating</span>
          </div></>}
        </div>}
      </div>

      {canMaintainHosts && (
        <HostMaintenancePanel
          apiClient={apiClient}
          setError={setError}
          canConfigureTopology={canConfigureTopology}
          canViewNodes={canViewNodes}
          canOperate={canOperate}
        />
      )}
      {canOperate && <div className="card full executor-install-card worker-management-card" id="executor-install" tabIndex={-1}>
        <div className="card-header">
          <div>
            <h3>Add Executor</h3>
            <p className="status">
              Generate an install command for a host that should connect back to this backend.
            </p>
          </div>
          <button type="button" onClick={() => void generateInstallCommand()} disabled={generatingInstall}>
            {generatingInstall ? 'Creating...' : 'Create and copy one-liner'}
          </button>
        </div>

        <div className="executor-install-grid">
          <div className="stack">
            <div className="executor-profile-toggle" role="group" aria-label="Executor install profile">
              <button
                type="button"
                className={installProfile === 'conversation' ? 'secondary active' : 'secondary'}
                aria-pressed={installProfile === 'conversation'}
                onClick={() => selectInstallProfile('conversation')}
              >
                Conversation
              </button>
              <button
                type="button"
                className={installProfile === 'production' ? 'secondary active' : 'secondary'}
                aria-pressed={installProfile === 'production'}
                onClick={() => selectInstallProfile('production')}
              >
                Production
              </button>
            </div>
            <div className="executor-profile-copy">
              {installProfile === 'conversation' ? (
                <p className="status">
                  Conversation executors process agent messages and require root, KVM, Firecracker,
                  kernel/rootfs artifacts, and a copy-on-write capable state root or configured fixed-size
                  btrfs store. The installer fails if required VM support is missing.
                </p>
              ) : (
                <p className="status">
                  Production executors run deploy and infrastructure helper commands, manage deployment
                  config on the host, and read service logs. They use infrastructure mode by default and
                  do not accept conversation VM leases unless you retag and reinstall them as conversation
                  executors.
                </p>
              )}
            </div>
          </div>

          <label>
            Executor name
            <input
              type="text"
              value={installName}
              onChange={(event) => setInstallName(event.target.value)}
              placeholder="defaults to host name"
            />
          </label>

          <label>
            Tags
            <input
              type="text"
              value={installTags}
              onChange={(event) => setInstallTags(event.target.value)}
              placeholder={profileDefaultTags[installProfile].join(', ')}
            />
            <span className="hint">
              Conversation tags must match queued work. Production tags should match server
              capabilities such as executor_tags and stay separate from conversation tags.
            </span>
          </label>

          {installProfile === 'production' && (
            <fieldset className="stack">
              <legend>Managed Btrfs storage</legend>
              <p className="hint">
                The installer verifies these paths before registration and configures systemd mount
                ordering so application data cannot silently fall back to the root filesystem.
              </p>
              <label>
                Application data root
                <input
                  type="text"
                  value={managedDataRoot}
                  onChange={(event) => setManagedDataRoot(event.target.value)}
                  placeholder="/create-proj/apps"
                />
              </label>
              <label>
                Snapshot staging root
                <input
                  type="text"
                  value={managedSnapshotRoot}
                  onChange={(event) => setManagedSnapshotRoot(event.target.value)}
                  placeholder="/srv/snapshots"
                />
              </label>
              <label>
                Expected Btrfs filesystem UUID
                <input
                  type="text"
                  value={managedFilesystemUuid}
                  onChange={(event) => setManagedFilesystemUuid(event.target.value)}
                  placeholder="read with: findmnt -no UUID /srv"
                />
                <span className="hint">Recommended: pins the executor to the intended RAID1 filesystem.</span>
              </label>
              <label>
                Minimum free space (%)
                <input
                  type="number"
                  min={5}
                  max={50}
                  value={managedMinFreePercent}
                  onChange={(event) => setManagedMinFreePercent(Number(event.target.value))}
                />
              </label>
              <label className="row row--compact">
                <input
                  type="checkbox"
                  checked={managedRequireRaid1}
                  onChange={(event) => setManagedRequireRaid1(event.target.checked)}
                />
                Require RAID1 data and metadata profiles
              </label>
            </fieldset>
          )}
        </div>

        {generatedInstall && (
          <div className="token-command token-command--secret executor-install-command">
            <code>{generatedInstall.command}</code>
            <button
              type="button"
              className="ghost"
              onClick={() => void copyText('install command', generatedInstall.command)}
            >
              {copied === 'install command' ? 'Copied' : 'Copy'}
            </button>
            <span className="hint">
              Temporary API key {generatedInstall.apiKeyId.slice(0, 8)} can install {generatedInstall.profile}{' '}
              executors until {formatTimestamp(generatedInstall.expiresAt)}. Each host creates its own
              executor token when the command runs.
            </span>
          </div>
        )}
      </div>}

      {canViewNodes && <div className="card full worker-management-card" id="executor-nodes" tabIndex={-1}>
        <div className="card-header">
          <h3>Executor Nodes</h3>
          <span className="hint">{hasLoaded ? `${nodes.length} records${loadFailed ? ' (previous)' : ''}` : 'Not loaded'}</span>
        </div>
        <div className="table-scroll responsive-table-scroll" role="region" aria-label="Executor nodes" tabIndex={0} aria-busy={loading}>
          <table className="responsive-table executor-table">
            <thead>
              <tr>
                <th scope="col">Executor</th>
                <th scope="col">Status</th>
                <th scope="col">Capabilities</th>
                <th scope="col">Firecracker</th>
                <th scope="col">Update</th>
                <th scope="col">Current Job</th>
                <th scope="col">Last Seen</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {nodes.map((node) => {
                const firecracker = firecrackerSummary(node);
                const checks = firecrackerChecks(node);
                const canDelete = !node.current_job_id && !node.current_run_id;
                const updateActive = selfUpdateActive(node);
                const fresh = heartbeatFreshFor(node);
                const awaitingReconnect = Boolean(
                  updateWatch?.workerId === node.id &&
                  updateWatch.waitingForReconnect &&
                  Date.now() < updateWatch.until &&
                  !node.runner_current
                );
                const runner = runnerStatus(
                  node,
                  awaitingReconnect
                );
                const availability = executorAvailabilityLabel(node, fresh);
                const backendHash = shortHash(node.backend_git_hash);
                const runnerHash = shortHash(node.capabilities?.runner_git_hash);
                return (
                  <tr key={node.id}>
                    <td data-label="Executor" className="executor-table__identity">
                      <div className="stack">
                        <strong>{node.name || node.id}</strong>
                        <span className="mono">{node.id}</span>
                        <span className="hint">
                          protocol {node.protocol_version}
                          {node.worker_version ? ` · ${node.worker_version}` : ''}
                        </span>
                        {node.capabilities?.hostname && (
                          <span className="hint">host {node.capabilities.hostname}</span>
                        )}
                        <span className={availability.className}>
                          {availability.label}
                        </span>
                        {node.system_default && <span className="badge badge--success">system default</span>}
                        <span className="hint">
                          Assurance: {node.assurance_class.replaceAll('_', ' ')} · {node.assurance_state}
                        </span>
                        <span className="hint">Owner: {executorOwnerLabel(node)}</span>
                        {executorOwnerDetail(node) && (
                          <span className="hint">{executorOwnerDetail(node)}</span>
                        )}
                      </div>
                    </td>
                    <td data-label="Status">
                      <span className={statusClass(statusLabel(node, fresh))}>{statusLabel(node, fresh)}</span>
                      {node.last_error && <p className="warning-text">{node.last_error}</p>}
                      {!fresh && <p className="warning-text">{heartbeatHintFor(node)}; queued helpers cannot run until reconnect.</p>}
                    </td>
                    <td data-label="Capabilities">
                      <div className="row">
                        <span className={capabilityEnabled(node.capabilities?.docker) ? 'badge badge--success' : 'badge'}>
                          docker
                        </span>
                        <span className={capabilityEnabled(node.capabilities?.event_streaming) ? 'badge badge--success' : 'badge'}>
                          events
                        </span>
                        <span className={capabilityEnabled(node.capabilities?.cancellation) ? 'badge badge--success' : 'badge'}>
                          cancel
                        </span>
                        <span className={capabilityEnabled(node.capabilities?.infrastructure_helpers) ? 'badge badge--success' : 'badge'}>
                          helpers
                        </span>
                      </div>
                      {node.tags.length > 0 && (
                        <p className="hint">{node.tags.map((tag) => `#${tag}`).join(' ')}</p>
                      )}
                      {node.capabilities?.managed_storage?.configured && (
                        <details className="worker-checks" open={!node.capabilities.managed_storage.ready}>
                          <summary>
                            <span className={node.capabilities.managed_storage.ready ? 'badge badge--success' : 'badge badge--error'}>
                              Btrfs storage {node.capabilities.managed_storage.ready ? 'ready' : 'blocked'}
                            </span>
                          </summary>
                          <div className="stack">
                            <span className="mono">{node.capabilities.managed_storage.data_root}</span>
                            <span className="hint">
                              {node.capabilities.managed_storage.filesystem || 'unknown filesystem'} · UUID{' '}
                              {node.capabilities.managed_storage.filesystem_uuid || 'unreported'} · free{' '}
                              {node.capabilities.managed_storage.free_percent ?? '—'}% (reserve{' '}
                              {node.capabilities.managed_storage.min_free_percent ?? '—'}%)
                            </span>
                            <span className="hint">
                              data {node.capabilities.managed_storage.data_profile || 'unreported'} · metadata{' '}
                              {node.capabilities.managed_storage.metadata_profile || 'unreported'}
                            </span>
                            <span className="hint">
                              protected send parents{' '}
                              {node.capabilities.managed_storage.retained_send_snapshots ?? 0} · restore baselines{' '}
                              {node.capabilities.managed_storage.baseline_snapshots ?? 0}
                            </span>
                            {(node.capabilities.managed_storage.blockers ?? []).map((blocker) => (
                              <span key={blocker} className="warning-text">{blocker}</span>
                            ))}
                          </div>
                        </details>
                      )}
                    </td>
                    <td data-label="Firecracker">
                      <span className={firecracker.className}>{firecracker.label}</span>
                      {checks.length > 0 && (
                        <details className="worker-checks">
                          <summary>Host checks</summary>
                          <div className="worker-check-grid">
                            {checks.map((check) => (
                              <span key={check.key} className={checkTextClass(check.severity)}>
                                <span className={checkDotClass(check.severity)} />
                                {check.label}
                              </span>
                            ))}
                          </div>
                        </details>
                      )}
                    </td>
                    <td data-label="Update">
                      <div className="stack">
                        <span className={runner.className}>{runner.label}</span>
                        {!scoped && <span className="hint">
                          runner <span className="mono">{runnerHash}</span> · backend{' '}
                          <span className="mono">{backendHash}</span>
                        </span>}
                        {node.capabilities?.runner_build_time && (
                          <span className="hint">built {formatTimestamp(node.capabilities.runner_build_time)}</span>
                        )}
                        {node.self_update && (
                          <details className="worker-checks" open={updateActive || node.self_update.status === 'failed'}>
                            <summary>
                              <span className={helperStatusClass(node.self_update.status)}>
                                self-update {node.self_update.status}
                              </span>
                            </summary>
                            <div className="stack">
                              <span className="hint">queued {formatTimestamp(node.self_update.created_at)}</span>
                              {node.self_update.leased_at && (
                                <span className="hint">started {formatTimestamp(node.self_update.leased_at)}</span>
                              )}
                              {node.self_update.finished_at && (
                                <span className="hint">finished {formatTimestamp(node.self_update.finished_at)}</span>
                              )}
                              {node.self_update.last_error && (
                                <span className="warning-text">{node.self_update.last_error}</span>
                              )}
                            </div>
                          </details>
                        )}
                        {node.self_update?.status?.toLowerCase() === 'succeeded' && !node.runner_current && (
                          <span className="warning-text">
                            {awaitingReconnect
                              ? 'update finished, but the executor has not re-registered with the new runner yet'
                              : 'last self-update succeeded, but this executor still reports an older runner'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td data-label="Current job">
                      {scoped ? 'Workload identity unavailable' : node.current_job_id ? (
                        <div className="stack">
                          <span className="mono">{node.current_job_id}</span>
                          {node.current_run_id && <span className="hint">{node.current_run_id}</span>}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td data-label="Last seen">
                      {formatTimestamp(node.last_seen_at)}
                      <br />
                      <span className={fresh ? 'hint' : 'warning-text'}>{heartbeatHintFor(node)}</span>
                    </td>
                    <td data-label="Actions" className="responsive-table__actions">
                      <div className="row row--compact">
                        <Link className="button secondary compact" to={`/executors/${encodeURIComponent(node.id)}`}>
                          Details
                        </Link>
                        {scoped && <ScopedSelfUpdate apiClient={apiClient} node={node} reload={loadWorkers} />}
                        {canOperate && node.access_level !== 'terminal' && (
                          <>
                            <button
                              type="button"
                              className="secondary compact"
                              disabled={updating === node.id}
                              onClick={() => void setSystemDefault(node, !node.system_default)}
                            >
                              {node.system_default ? 'Unset default' : 'Set default'}
                            </button>
                            <button
                              type="button"
                              className="secondary compact"
                              disabled={
                                !canSelfUpdate(node) ||
                                updating === node.id ||
                                bulkUpdating
                              }
                              onClick={() => void drainAndUpdateNode(node)}
                              title="Drain new conversation work, queue self-update, then resume conversations after success"
                            >
                              {updating === node.id
                                ? 'Queuing...'
                                : updateActive
                                  ? node.self_update?.status === 'running'
                                    ? 'Updating...'
                                    : 'Update queued'
                                  : !fresh
                                    ? 'Queue drain update'
                                    : 'Drain update'}
                            </button>
                            <button
                              type="button"
                              className="secondary compact"
                              disabled={
                                !canSelfUpdate(node) ||
                                updating === node.id ||
                                bulkUpdating
                              }
                              onClick={() => void forceUpdateNode(node)}
                            >
                              {updating === node.id
                                ? 'Queuing...'
                                : updateActive
                                  ? node.self_update?.status === 'running'
                                    ? 'Updating...'
                                    : 'Update queued'
                                  : !fresh
                                    ? 'Queue for reconnect'
                                    : 'Force update'}
                            </button>
                            <button
                              type="button"
                              className="danger compact"
                              disabled={!canDelete || deleting === node.id}
                              onClick={() => void deleteNode(node)}
                            >
                              {deleting === node.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {nodes.length === 0 && (
                <tr className="responsive-table__empty">
                  <td colSpan={8}>
                    <p className="status">{loading ? 'Loading executor nodes…' : loadFailed ? 'Executor nodes unavailable. Use Refresh to retry.' : 'No executor nodes have registered yet.'}
                    {hasLoaded && !loading && !loadFailed && canOperate && <a href="#executor-install"> Add an executor to register your first host.</a>}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>}

      {canOperate && <div className="card full worker-management-card" id="executor-tokens" tabIndex={-1}>
        <div className="card-header">
          <h3>Executor Tokens</h3>
          <span className="hint">{hasLoaded ? `${tokens.length} tokens${loadFailed ? ' (previous)' : ''}` : 'Not loaded'}</span>
        </div>
        <div className="table-scroll responsive-table-scroll" role="region" aria-label="Executor tokens" tabIndex={0} aria-busy={loading}>
          <table className="responsive-table executor-token-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Tags</th>
                <th scope="col">Status</th>
                <th scope="col">Last Used</th>
                <th scope="col">Expires</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((token) => (
                <tr key={token.id}>
                  <td data-label="Name" className="executor-table__identity">
                    <strong>{token.name || token.id}</strong>
                    <div className="mono">{token.id}</div>
                  </td>
                  <td data-label="Tags">{token.tags.length ? token.tags.join(', ') : '-'}</td>
                  <td data-label="Status">
                    <span
                      className={
                        token.revoked_at || executorTokenExpired(token)
                          ? 'badge badge--error'
                          : 'badge badge--success'
                      }
                    >
                      {token.revoked_at ? 'revoked' : executorTokenExpired(token) ? 'expired' : 'active'}
                    </span>
                  </td>
                  <td data-label="Last used">{formatTimestamp(token.last_used_at)}</td>
                  <td data-label="Expires">{token.expires_at ? formatTimestamp(token.expires_at) : 'Never'}</td>
                  <td data-label="Actions" className="responsive-table__actions">
                    <button
                      type="button"
                      className="danger compact"
                      disabled={Boolean(token.revoked_at) || revokingToken === token.id}
                      onClick={() => void revokeToken(token)}
                    >
                      {revokingToken === token.id ? 'Revoking...' : 'Revoke'}
                    </button>
                  </td>
                </tr>
              ))}
              {tokens.length === 0 && (
                <tr className="responsive-table__empty">
                  <td colSpan={6}>
                    <p className="status">{loading ? 'Loading executor tokens…' : loadFailed ? 'Executor tokens unavailable. Use Refresh to retry.' : 'No executor tokens have been created. Each host creates its own token when the install command runs.'}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>}
    </section>
  );
}
